import {
  PermissionContext,
  permissionsOperations,
} from '@ardoq/access-control';
import {
  AnyWidgetAttributes,
  APIChartWidgetAttributes,
  APIDashboardAttributes,
  APIFieldAttributes,
  APIReportAttributes,
  APIWorkspaceAttributes,
  ArdoqId,
  ReportColumn,
  ReportColumnType,
  WidgetDatasource,
  WidgetDataSourceTypes,
} from '@ardoq/api-types';
import { dateRangeOperations } from '@ardoq/date-range';
import { isEqual } from '@ardoq/common-helpers';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { fields$ } from 'streams/fields/fields$';
import { StreamCollection } from 'streams/utils/streamUtils';
import { persistentWorkspaces$ as workspaces$ } from 'streams/workspaces/workspaces$';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';

/**
 * Extended accessibility information for a workspace that is not sent directly
 * from the backend. Currently only considers, but is not limited to,
 * confidential fields. Extend as needed as long as it is permission adjacent.
 */
type AccessibilityInfo = {
  confidentialFields: Set<string>;
  /**
   * An inversion of the PermissionContext's "confidentialFieldsTheUserCanSee"
   * that helps us determine if the user is missing access to any fields in a
   * workspace.
   */
  cannotSee: Set<string>;
  // Subset of `cannotSee` that are genuine date-range fields (by field type).
  dateRangeBaseNames: Set<string>;
};

export type WorkspaceFieldAccessibilityMap = Record<
  ArdoqId,
  AccessibilityInfo | undefined
>;

export const containsConfidentialFields = (
  accessibilityMap: WorkspaceFieldAccessibilityMap,
  workspaceId: ArdoqId
): boolean => {
  const accessibilityInfo = accessibilityMap[workspaceId];
  return !!accessibilityInfo && accessibilityInfo.confidentialFields.size > 0;
};

const columnMatchesInaccessibleField = (
  column: ReportColumn,
  { cannotSee, dateRangeBaseNames }: AccessibilityInfo
): boolean => {
  if (cannotSee.has(column.key)) return true;
  return (
    dateRangeOperations.fieldNameIsPartOfDateRangeField(column.key) &&
    dateRangeBaseNames.has(
      dateRangeOperations.extractDateRangeFieldName(column.key)
    )
  );
};

// True when the report's columns use a confidential field the user can't see in
// at least one queried workspace. Used to disable the report "Copy" action.
export const reportHasInaccessibleConfidentialFields = (
  accessibilityMap: WorkspaceFieldAccessibilityMap,
  {
    columns,
    workspaceIds,
  }: Pick<APIReportAttributes, 'columns' | 'workspaceIds'>
): boolean => {
  if (!workspaceIds || workspaceIds.length === 0) return false;
  const fieldColumns = columns.filter(
    column => column.type === ReportColumnType.FIELD
  );
  if (fieldColumns.length === 0) return false;
  return workspaceIds.some(workspaceId => {
    const accessibilityInfo = accessibilityMap[workspaceId];
    if (!accessibilityInfo) return false;
    return fieldColumns.some(column =>
      columnMatchesInaccessibleField(column, accessibilityInfo)
    );
  });
};

const isReportWidget = (
  widget: AnyWidgetAttributes
): widget is APIChartWidgetAttributes & { datasource: WidgetDatasource } =>
  'datasource' in widget &&
  widget.datasource.sourceType === WidgetDataSourceTypes.REPORT;

export const getDashboardReportWidgetSourceIds = (
  widgets: AnyWidgetAttributes[]
): ArdoqId[] =>
  widgets.filter(isReportWidget).map(widget => widget.datasource.sourceId);

export const dashboardHasInaccessibleConfidentialFields = (
  accessibilityMap: WorkspaceFieldAccessibilityMap,
  reportsById: Record<
    ArdoqId,
    Pick<APIReportAttributes, 'columns' | 'workspaceIds'> | undefined
  >,
  { widgets }: Pick<APIDashboardAttributes, 'widgets'>
): boolean =>
  getDashboardReportWidgetSourceIds(widgets).some(reportId => {
    const report = reportsById[reportId];
    return (
      report !== undefined &&
      reportHasInaccessibleConfidentialFields(accessibilityMap, report)
    );
  });

export const buildWorkspaceFieldAccessibilityMap = (
  permissionContext: PermissionContext,
  fieldsState: StreamCollection<APIFieldAttributes>,
  workspacesState: StreamCollection<APIWorkspaceAttributes>
): WorkspaceFieldAccessibilityMap => {
  const confidentialFieldNames = new Set(
    permissionsOperations.getConfidentialFields(permissionContext)
  );

  const modelIdToWorkspaces = new Map<ArdoqId, Set<ArdoqId>>();
  for (const workspace of workspacesState.models) {
    let workspaces = modelIdToWorkspaces.get(workspace.componentModel);
    if (!workspaces) {
      workspaces = new Set();
      modelIdToWorkspaces.set(workspace.componentModel, workspaces);
    }
    workspaces.add(workspace._id);
  }

  const workspaceToConfidentialFields = new Map<
    ArdoqId,
    APIFieldAttributes[]
  >();
  for (const field of fieldsState.models) {
    if (!confidentialFieldNames.has(field.name)) continue;
    const workspaces = modelIdToWorkspaces.get(field.model);
    if (!workspaces) continue;
    for (const workspaceId of workspaces) {
      let fields = workspaceToConfidentialFields.get(workspaceId);
      if (!fields) {
        fields = [];
        workspaceToConfidentialFields.set(workspaceId, fields);
      }
      fields.push(field);
    }
  }

  const result: WorkspaceFieldAccessibilityMap = {};
  for (const [workspaceId, fields] of workspaceToConfidentialFields) {
    const confidentialFields = new Set<string>();
    const cannotSee = new Set<string>();
    const dateRangeBaseNames = new Set<string>();
    for (const field of fields) {
      const isDateRangeField =
        dateRangeOperations.fieldIsPartOfDateRangeField(field);
      const confidentialFieldName = isDateRangeField
        ? dateRangeOperations.extractDateRangeFieldName(field.name)
        : field.name;
      confidentialFields.add(confidentialFieldName);
      if (
        !permissionsOperations.canSeeConfidentialFieldByName(
          permissionContext,
          field.name,
          workspaceId
        )
      ) {
        cannotSee.add(confidentialFieldName);
        if (isDateRangeField) {
          dateRangeBaseNames.add(confidentialFieldName);
        }
      }
    }
    result[workspaceId] = { confidentialFields, cannotSee, dateRangeBaseNames };
  }
  return result;
};

export const workspaceFieldAccessibility$ = combineLatest([
  currentUserPermissionContext$,
  fields$,
  workspaces$,
]).pipe(
  map(([permissionContext, fieldsState, workspacesState]) =>
    buildWorkspaceFieldAccessibilityMap(
      permissionContext,
      fieldsState,
      workspacesState
    )
  ),
  distinctUntilChanged(isEqual),
  shareReplay({ bufferSize: 1, refCount: true })
);
